import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { translate } from 'utils/translations'
import * as Styled from './styled'
import { usePositions } from './hooks/use-positions'
import { IZOrderContextMenuProps } from './types'
import { MENU_ROWS } from './consts'

const ZOrderContextMenu: React.FC<IZOrderContextMenuProps> = ({
  position,
  onClose,
  onSelect
}) => {
  const panelRef = useRef<HTMLDivElement>(null)

  const { left, top } = usePositions(position)

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  useEffect(() => {
    let removeListener: (() => void) | undefined
    const timeoutId = window.setTimeout(() => {
      const handlePointerDown = (e: MouseEvent | PointerEvent) => {
        if (!panelRef.current?.contains(e.target as Node)) {
          onClose()
        }
      }
      document.addEventListener('pointerdown', handlePointerDown, true)
      removeListener = () =>
        document.removeEventListener('pointerdown', handlePointerDown, true)
    }, 0)

    return () => {
      window.clearTimeout(timeoutId)
      removeListener?.()
    }
  }, [onClose])

  return createPortal(
    <>
      <Styled.Backdrop aria-hidden />
      <Styled.MenuPanel
        ref={panelRef}
        role="menu"
        style={{ left, top }}
        onPointerDown={e => e.stopPropagation()}
      >
        {MENU_ROWS.map(row =>
          row.type === 'divider' ? (
            <Styled.MenuDivider key={`divider-${row.id}`} role="separator" />
          ) : (
            <Styled.MenuItem
              key={row.action}
              type="button"
              role="menuitem"
              onClick={() => {
                onSelect(row.action)
                onClose()
              }}
            >
              {row.icon}
              {translate(row.labelKey)}
            </Styled.MenuItem>
          )
        )}
      </Styled.MenuPanel>
    </>,
    document.body
  )
}

export default ZOrderContextMenu
